// The composable broadcast filter builder (segmentation slice, Stage 4).
//
// Renders the `rules` of a `{ kind: 'filter' }` audience as editable rows:
// dimension + operator + value, AND-combined, max MAX_SEGMENT_RULES. Each row
// reads like a sentence — "Stamp count · is at least · 5 stamps". The shared
// SegmentRule discriminated union is the source of truth for what's valid;
// this component only ever emits rules that satisfy it.

import {
  LOYALTY_MAX_STAMPS,
  MAX_SEGMENT_RULES,
  audienceSummary,
  type SegmentRule,
} from '@naya/shared'

type Dimension = SegmentRule['dimension']

// Dimension picker copy.
const DIMENSIONS: { value: Dimension; label: string }[] = [
  { value: 'stamps', label: 'Stamp count' },
  { value: 'milestone_gap', label: 'Stamps from a reward' },
  { value: 'last_stamp', label: 'Last stamp earned' },
  { value: 'enrolled', label: 'When they joined' },
  { value: 'reward_state', label: 'Reward status' },
  { value: 'birthday', label: 'Birthday' },
]

// Operator copy, per dimension. The key order is the dropdown order.
const OPERATORS: Record<Dimension, { value: string; label: string }[]> = {
  stamps: [
    { value: 'gte', label: 'is at least' },
    { value: 'lte', label: 'is at most' },
    { value: 'eq', label: 'is exactly' },
    { value: 'between', label: 'is between' },
  ],
  milestone_gap: [
    { value: 'lte', label: 'is at most' },
    { value: 'eq', label: 'is exactly' },
  ],
  last_stamp: [
    { value: 'within_days', label: 'within the last' },
    { value: 'older_than_days', label: 'more than' },
  ],
  enrolled: [
    { value: 'within_days', label: 'within the last' },
    { value: 'older_than_days', label: 'more than' },
  ],
  reward_state: [
    { value: 'has_unclaimed', label: 'has an unclaimed reward' },
    { value: 'no_stamps', label: 'has no stamps yet' },
    { value: 'completed_before', label: 'completed a card before' },
  ],
  birthday: [{ value: 'this_month', label: 'is this month' }],
}

// The unit word shown after the value input.
function unitFor(dim: Dimension): string {
  if (dim === 'stamps' || dim === 'milestone_gap') return 'stamps'
  return 'days'
}

// A trailing clause for date dimensions so the row reads as a sentence
// ("more than 60 days · ago").
function trailingFor(rule: SegmentRule): string | null {
  if (
    (rule.dimension === 'last_stamp' || rule.dimension === 'enrolled') &&
    rule.op === 'older_than_days'
  ) {
    return 'ago'
  }
  return null
}

// A blank rule for a freshly-added row / a dimension switch.
export function defaultRule(dim: Dimension): SegmentRule {
  switch (dim) {
    case 'stamps':
      return { dimension: 'stamps', op: 'gte', value: 5 }
    case 'milestone_gap':
      return { dimension: 'milestone_gap', op: 'lte', value: 2 }
    case 'last_stamp':
      return { dimension: 'last_stamp', op: 'within_days', value: 30 }
    case 'enrolled':
      return { dimension: 'enrolled', op: 'within_days', value: 7 }
    case 'reward_state':
      return { dimension: 'reward_state', op: 'has_unclaimed' }
    case 'birthday':
      return { dimension: 'birthday', op: 'this_month' }
  }
}

// Whether a rule's dimension/operator combination needs a numeric value.
function hasValue(rule: SegmentRule): boolean {
  return rule.dimension !== 'reward_state' && rule.dimension !== 'birthday'
}

// Apply a new operator to a rule, keeping it a valid union member. The only
// subtlety is `stamps` + `between`, which needs an upper bound.
function withOperator(rule: SegmentRule, op: string): SegmentRule {
  if (rule.dimension === 'stamps') {
    if (op === 'between') {
      return {
        dimension: 'stamps',
        op: 'between',
        value: rule.value,
        value2: rule.value2 ?? Math.min(rule.value + 2, LOYALTY_MAX_STAMPS),
      }
    }
    return {
      dimension: 'stamps',
      op: op as 'eq' | 'gte' | 'lte',
      value: rule.value,
    }
  }
  // Every other dimension keeps its value/structure; only `op` changes.
  return { ...rule, op } as SegmentRule
}

function clampInt(raw: string, min: number, max: number): number {
  const n = Number.parseInt(raw, 10)
  if (Number.isNaN(n)) return min
  return Math.min(max, Math.max(min, n))
}

function RuleRow({
  rule,
  index,
  joinWord,
  canRemove,
  onChange,
  onRemove,
}: {
  rule: SegmentRule
  index: number
  /** The connector shown above rows after the first — "and" or "or". */
  joinWord: 'and' | 'or'
  canRemove: boolean
  onChange: (r: SegmentRule) => void
  onRemove: () => void
}) {
  const ops = OPERATORS[rule.dimension]
  const unit = unitFor(rule.dimension)
  const trailing = trailingFor(rule)
  const valueMax = rule.dimension === 'stamps' ? LOYALTY_MAX_STAMPS : 365
  const valueMin = rule.dimension === 'stamps' ? 0 : 1

  return (
    <div className="filter-rule">
      {index > 0 && <span className="filter-rule-and">{joinWord}</span>}
      <div className="filter-rule-controls">
        <select
          className="owner-input filter-rule-dim"
          value={rule.dimension}
          aria-label="Filter by"
          onChange={(e) =>
            onChange(defaultRule(e.target.value as Dimension))
          }
        >
          {DIMENSIONS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>

        <select
          className="owner-input filter-rule-op"
          value={rule.op}
          aria-label="Condition"
          onChange={(e) => onChange(withOperator(rule, e.target.value))}
        >
          {ops.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        {hasValue(rule) && (
          <span className="filter-rule-value">
            <input
              className="owner-input filter-rule-num"
              type="number"
              inputMode="numeric"
              min={valueMin}
              max={valueMax}
              value={'value' in rule ? rule.value : 0}
              aria-label="Value"
              onChange={(e) =>
                onChange({
                  ...rule,
                  value: clampInt(e.target.value, valueMin, valueMax),
                } as SegmentRule)
              }
            />
            {rule.dimension === 'stamps' && rule.op === 'between' && (
              <>
                <span className="filter-rule-unit">and</span>
                <input
                  className="owner-input filter-rule-num"
                  type="number"
                  inputMode="numeric"
                  min={valueMin}
                  max={valueMax}
                  value={rule.value2 ?? rule.value}
                  aria-label="Upper value"
                  onChange={(e) =>
                    onChange({
                      ...rule,
                      value2: clampInt(e.target.value, valueMin, valueMax),
                    })
                  }
                />
              </>
            )}
            <span className="filter-rule-unit">
              {unit}
              {trailing ? ` ${trailing}` : ''}
            </span>
          </span>
        )}

        {canRemove && (
          <button
            type="button"
            className="filter-rule-remove"
            aria-label="Remove this filter"
            onClick={onRemove}
          >
            ×
          </button>
        )}
      </div>
    </div>
  )
}

/** The match mode + rule list of a `{ kind: 'filter' }` audience. */
export interface FilterDraft {
  match: 'all' | 'any'
  rules: SegmentRule[]
}

export function BroadcastFilterBuilder({
  match,
  rules,
  onChange,
}: {
  match: 'all' | 'any'
  rules: SegmentRule[]
  onChange: (next: FilterDraft) => void
}) {
  const canAdd = rules.length < MAX_SEGMENT_RULES
  const canRemove = rules.length > 1
  const joinWord = match === 'any' ? 'or' : 'and'
  const multi = rules.length > 1

  return (
    <div className="filter-builder">
      <div className="filter-builder-head">
        <span className="filter-builder-lead">Customers who match</span>
        {/* The match toggle only bites with 2+ rules, but stays visible so
            the owner can set intent before adding the second filter. */}
        <div
          className="filter-match-toggle"
          role="radiogroup"
          aria-label="Match mode"
        >
          {(['all', 'any'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={match === m}
              className={`filter-match-opt${match === m ? ' is-selected' : ''}`}
              onClick={() => onChange({ match: m, rules })}
            >
              {m}
            </button>
          ))}
        </div>
        <span className="filter-builder-lead">
          {multi ? 'of these:' : 'this:'}
        </span>
      </div>

      <div className="filter-builder-rules">
        {rules.map((rule, i) => (
          <RuleRow
            key={i}
            rule={rule}
            index={i}
            joinWord={joinWord}
            canRemove={canRemove}
            onChange={(r) =>
              onChange({
                match,
                rules: rules.map((existing, j) => (j === i ? r : existing)),
              })
            }
            onRemove={() =>
              onChange({ match, rules: rules.filter((_, j) => j !== i) })
            }
          />
        ))}
      </div>

      {canAdd && (
        <button
          type="button"
          className="filter-builder-add"
          onClick={() =>
            onChange({ match, rules: [...rules, defaultRule('stamps')] })
          }
        >
          + Add another filter
        </button>
      )}
      {!canAdd && (
        <p className="filter-builder-max">
          Up to {MAX_SEGMENT_RULES} filters per audience.
        </p>
      )}

      {/* Plain-English read-back of the rules. */}
      <p className="filter-builder-summary">
        {audienceSummary({ kind: 'filter', match, rules })}
      </p>
    </div>
  )
}
